import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IChatConversation, IChatConversationScope, IPromptAuditEntry } from 'src/app/model/chat-conversation';
import { IAnthropic } from 'src/app/model/configuration/anthropic';
import { IConfiguration } from 'src/app/model/configuration/configuration';
import { ICopilotPrompting } from 'src/app/model/configuration/copilot-prompting';
import { ICsv } from 'src/app/model/configuration/csv';
import { IEmail } from 'src/app/model/configuration/email';
import { IGemini } from 'src/app/model/configuration/gemini';
import { IModelCatalogs, IProviderModelCatalog, IProviderModelCatalogDiff, ModelCatalogProvider } from 'src/app/model/configuration/model-catalog';
import { INotification } from 'src/app/model/configuration/notification';
import { IOllama } from 'src/app/model/configuration/ollama';
import { IOpenai } from 'src/app/model/configuration/openai';
import { ConfigurationServiceImpl } from './implementation/configuration.service-impl';

export interface Cert {
  certificate: string;
  firstSeen: string;
  trusted: boolean;
  principal: string;
  issuer: string;
  fingerprint: string;
  comment: string;
}

@Injectable({
  providedIn: 'root',
  useClass: ConfigurationServiceImpl
})
export abstract class ConfigurationService {

  public abstract getConfiguration(): Promise<IConfiguration>;

  public abstract updateEmailConfiguration(email: IEmail): Observable<IConfiguration>;

  public abstract updateCsvConfiguration(csv: ICsv): Observable<IConfiguration>;

  public abstract updateNotificationConfiguration(notification: INotification): Observable<IConfiguration>;

  public abstract updateOpcConfiguration(opc: IConfiguration['opc'] & { password: string | undefined }): Observable<IConfiguration>;

  public abstract getOpcTrustedCertificates(): Observable<Cert[]>;

  public abstract getOpcQuarantinedCertificates(): Observable<Cert[]>;

  public abstract addOpcTrustedCertificates(cert: Cert): Observable<void>;

  public abstract revokeOpcTrustedCertificates(cert: Cert): Observable<void>;

  public abstract getCopilotPromptingSettings(): Promise<ICopilotPrompting>;

  public abstract updateCopilotPromptingConfiguration(copilotPrompting: ICopilotPrompting): Observable<IConfiguration>;

  public abstract updateOpenaiConfiguration(openai: IOpenai): Observable<IConfiguration>;

  public abstract getOpenaiSettings(): Promise<IOpenai>;

  public abstract getAnthropicSettings(): Promise<IAnthropic>;

  public abstract updateAnthropicConfiguration(anthropic: IAnthropic): Observable<IConfiguration>;

  public abstract getGeminiSettings(): Promise<IGemini>;

  public abstract updateGeminiConfiguration(gemini: IGemini): Observable<IConfiguration>;

  public abstract getOllamaSettings(): Promise<IOllama>;

  public abstract updateOllamaConfiguration(ollama: IOllama): Observable<IConfiguration>;

  public abstract getOllamaVisionModelCatalog(): Promise<IProviderModelCatalog>;

  public abstract resolveOllamaVisionModelCatalog(ollama: IOllama): Promise<IProviderModelCatalog>;

  public abstract getModelCatalogs(): Promise<IModelCatalogs>;

  public abstract getModelCatalog(provider: ModelCatalogProvider): Promise<IProviderModelCatalog>;

  public abstract getModelCatalogDiff(provider: ModelCatalogProvider): Promise<IProviderModelCatalogDiff>;

  public abstract refreshModelCatalog(provider: ModelCatalogProvider): Promise<IProviderModelCatalogDiff>;

  public abstract createChatkitSession(): Promise<{ client_secret: string; promptAudit?: IPromptAuditEntry }>;

  public abstract syncChatkitThread(
    threadId: string,
    promptAuditTrail?: IPromptAuditEntry[],
    conversationScope?: IChatConversationScope | null
  ): Promise<IChatConversation>;
}
